<script setup lang="ts">
/**
 * Files: everything you uploaded and everything tools made for you
 * (transformed, vectorized, generated images, SVGs, 3D models, previews) —
 * preview, download, rename, keep longer, share, delete, and "use with"
 * shortcuts that open the right tool with the file filled in.
 */
import { Box, ChevronRight, Clock, Copy, Download, FileImage, FileText, Film, Images, Pencil, Share2, Trash2, Upload, Wand2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useClipboard } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { toast } from "vue-sonner";
import { useMyTools } from "@/api/me";
import { deleteFile, fileUrl, isImage, MAX_UPLOAD_BYTES, thumbnailUrl, updateFile, uploadFile, useFiles, type FileFilter, type FileItem } from "@/api/uploads";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import { actionsFor, originLabel } from "@/components/files/actions";
import ShareFileDialog from "@/components/files/ShareFileDialog.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { errorMessage } from "@/lib/errors";
import { formatDateTime, formatUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Files", layout: "wide" } });

const PAGE = 60;
const queryClient = useQueryClient();
const { can } = useSession();
const tools = useMyTools();
const toolNames = computed(() => new Set((tools.data.value ?? []).map((x) => x.name)));
const { copy } = useClipboard({ legacy: true });

const origin = useRouteQuery("origin", "");
const kind = useRouteQuery("kind", "");
const q = useRouteQuery("q", "");
const selectedId = useRouteQuery("file", "");
const limit = ref(PAGE);
watch([origin, kind, q], () => (limit.value = PAGE));

const filter = computed<FileFilter>(() => ({
  ...(origin.value ? { origin: origin.value as FileFilter["origin"] } : {}),
  ...(kind.value ? { kind: kind.value as FileFilter["kind"] } : {}),
  ...(q.value.trim() ? { q: q.value.trim() } : {}),
  limit: limit.value,
}));
const files = useFiles(filter);
const items = computed(() => files.data.value?.items ?? []);
const usage = computed(() => files.data.value?.usage);
const selected = computed(() => items.value.find((f) => f.id === selectedId.value) ?? null);
const sheetOpen = computed({ get: () => Boolean(selected.value), set: (v) => !v && (selectedId.value = "") });

const ORIGINS = [
  { value: "", label: "All" },
  { value: "uploaded", label: "Uploaded" },
  { value: "generated", label: "Made by tools" },
];
const KINDS = [
  { value: "image", label: "Images" },
  { value: "svg", label: "SVG" },
  { value: "model", label: "3D models" },
  { value: "video", label: "Videos" },
  { value: "pdf", label: "PDF" },
  { value: "other", label: "Other" },
];

const size = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const iconFor = (f: FileItem) => (f.mimeType.startsWith("model/") ? Box : f.mimeType.startsWith("video/") ? Film : isImage(f) ? FileImage : FileText);
const daysLeft = (f: FileItem) => Math.max(0, Math.round((new Date(f.expiresAt).getTime() - Date.now()) / 86_400_000));
/** Browsers show these directly; others (TIFF, …) use the server's WebP thumbnail. */
const previewSrc = (f: FileItem) => (/^image\/(png|jpeg|gif|webp|svg\+xml)$/.test(f.mimeType) ? fileUrl(f.id) : thumbnailUrl(f.id));

async function refresh() {
  await queryClient.invalidateQueries({ queryKey: ["files"] });
}

// --- upload (button or drop)

const fileInput = ref<HTMLInputElement | null>(null);
const uploading = ref(0);
const dragging = ref(false);

async function upload(list: FileList | File[] | null | undefined) {
  const picked = [...(list ?? [])];
  if (!picked.length) return;
  uploading.value += picked.length;
  for (const file of picked) {
    try {
      if (file.size > MAX_UPLOAD_BYTES) throw new Error(`${file.name} is larger than ${MAX_UPLOAD_BYTES / 1024 / 1024} MB`);
      const up = await uploadFile(file);
      toast.success(`Uploaded ${up.name}`);
      if (picked.length === 1) selectedId.value = up.id;
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      uploading.value--;
    }
  }
  await refresh();
}

function onDrop(e: DragEvent) {
  dragging.value = false;
  void upload(e.dataTransfer?.files);
}

// --- manage

const renaming = ref(false);
const newName = ref("");
watch(selected, () => {
  renaming.value = false;
  newName.value = selected.value?.name ?? "";
});

async function rename(f: FileItem) {
  const name = newName.value.trim();
  if (!name || name === f.name) return void (renaming.value = false);
  try {
    await updateFile(f.id, { name });
    renaming.value = false;
    toast.success("Renamed.");
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const keepOptions = computed(() => {
  const max = usage.value?.maxKeepDays ?? 90;
  return [7, 30, 90, 180, 365].filter((d) => d <= max).map((d) => ({ value: String(d), label: `${d} days from now` }));
});

async function keep(f: FileItem, days: string | undefined) {
  if (!days) return;
  try {
    await updateFile(f.id, { keepDays: Number(days) });
    toast.success(`Kept until ${formatDateTime(Date.now() + Number(days) * 86_400_000)}.`);
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function remove(f: FileItem) {
  const ok = await confirm({ title: `Delete ${f.name}?`, description: "The file is deleted now. Shares made from it keep working (they hold their own copy).", confirmLabel: "Delete", destructive: true });
  if (!ok) return;
  try {
    await deleteFile(f.id);
    selectedId.value = "";
    toast.success("Deleted.");
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const shareOpen = ref(false);
const shareTarget = ref<FileItem | null>(null);
function share(f: FileItem) {
  shareTarget.value = f;
  shareOpen.value = true;
}

// --- details (provenance)

interface Detail {
  label: string;
  value: string;
  file?: string;
}
const META_LABELS: Record<string, string> = {
  operation: "Operation", engine: "Engine", provider: "Provider", model: "Model", task: "Task", prompt: "Prompt", purpose: "Purpose",
  format: "Format", renderer: "Renderer", caption: "Caption", via: "Uploaded via", taskId: "Image-processor task",
};

function details(f: FileItem): Detail[] {
  const out: Detail[] = [];
  const meta = f.meta as Record<string, unknown>;
  for (const [key, label] of Object.entries(META_LABELS)) {
    const v = meta[key];
    if (typeof v === "string" && v) out.push({ label, value: v });
  }
  if (typeof meta.costUsd === "number") out.push({ label: "Cost", value: formatUsd(meta.costUsd) });
  const from = Array.isArray(meta.from) ? meta.from : meta.from ? [meta.from] : [];
  for (const ref of from) {
    if (typeof ref !== "string") continue;
    const id = /^upload:\/\/([0-9a-f-]{36})$/.exec(ref)?.[1];
    out.push({ label: "Made from", value: id ? (items.value.find((x) => x.id === id)?.name ?? ref) : ref, file: id });
  }
  return out;
}
const actions = computed(() => (selected.value ? actionsFor(selected.value, toolNames.value) : []));
</script>

<template>
  <div
    class="relative"
    @dragover.prevent="dragging = true"
    @dragleave.self="dragging = false"
    @drop.prevent="onDrop"
  >
    <PageHeader title="Files" description="What you uploaded and what tools made for you — transformed, vectorized and generated images, SVGs, 3D models. Files are private, kept for 7 days unless you keep them longer, and can be shared or used with tools.">
      <template v-if="usage" #meta>
        <p class="mt-2 text-xs text-muted-foreground tabular-nums">{{ size(usage.bytes) }} of {{ size(usage.quotaBytes) }} used</p>
      </template>
      <template #actions>
        <input ref="fileInput" type="file" multiple class="sr-only" tabindex="-1" @change="(e) => upload((e.target as HTMLInputElement).files)" />
        <Button :loading="uploading > 0" @click="fileInput?.click()"><Upload /> Upload</Button>
      </template>
    </PageHeader>

    <FilterBar v-model:search="q" placeholder="Search names…" search-label="Search files" label="Filter files" class="mb-5">
      <SimpleSelect v-model="kind" :options="KINDS" allow-empty empty-label="All kinds" class="w-full sm:w-48" aria-label="Kind" />
      <template #chips><FilterChips v-model="origin" :options="ORIGINS" label="Origin" /></template>
    </FilterBar>

    <details class="group surface mb-6 overflow-hidden text-sm">
      <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 font-medium transition-colors hover:bg-state-hover [&::-webkit-details-marker]:hidden">
        <ChevronRight class="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
        Using files with Claude
      </summary>
      <ul class="list-disc space-y-1 border-t py-3 pr-4 pl-10 text-muted-foreground">
        <li>In claude.ai, attach or name the image and ask, e.g. "remove the background of this logo". Claude opens an upload box in the chat (<code>media_files</code>): drop the file there once, and it continues.</li>
        <li>Vectorize a logo with the Smake image-processor's AI agent: ask "vectorize this logo with the image-processor agent" (<code>media_vectorize engine=agent</code>), or pick a file here → <em>Vectorize with AI agent</em>.</li>
        <li>Every file has a reference like <code>upload://…</code> (copy it below) that you can paste into a chat or a tool form. Ask Claude "list my files" to find earlier results.</li>
        <li>Share a file with people inside or outside Smake: <em>Share</em> puts a copy behind a password and/or Smake sign-in.</li>
      </ul>
    </details>

    <div v-if="dragging" class="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-xl border-2 border-dashed border-primary bg-background/80 text-lg font-medium">Drop to upload</div>

    <ListSkeleton v-if="files.isPending.value" :rows="4" />
    <ErrorState v-else-if="files.error.value" :error="files.error.value" @retry="files.refetch()" />
    <EmptyState
      v-else-if="!items.length"
      :icon="Images"
      :title="origin || kind || q ? 'No files match' : 'No files yet'"
      description="Upload a logo or image here (or drop it on the page), or ask Claude to transform, vectorize or generate one."
    />
    <template v-else>
      <ul class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
        <li v-for="f in items" :key="f.id">
          <button
            type="button"
            :class="cn('surface surface-interactive flex h-full w-full flex-col overflow-hidden text-left', selectedId === f.id && 'border-primary ring-1 ring-primary')"
            @click="selectedId = f.id"
          >
            <div class="checkerboard grid aspect-[4/3] place-items-center overflow-hidden border-b">
              <img v-if="isImage(f)" :src="thumbnailUrl(f.id)" :alt="f.name" loading="lazy" class="max-h-full max-w-full object-contain" />
              <component :is="iconFor(f)" v-else class="size-10 text-muted-foreground" aria-hidden="true" />
            </div>
            <div class="flex min-w-0 flex-col gap-1 p-3">
              <span class="truncate text-sm font-medium" :title="f.name">{{ f.name }}</span>
              <span class="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Badge :variant="f.source === 'upload' ? 'muted' : 'info'">{{ originLabel(f.source) }}</Badge>
                {{ size(f.size) }} · <RelativeTime :value="f.createdAt" />
              </span>
            </div>
          </button>
        </li>
      </ul>
      <div v-if="(files.data.value?.total ?? 0) > items.length" class="mt-6 flex justify-center">
        <Button variant="outline" :disabled="files.isFetching.value" @click="limit += PAGE">Show more ({{ (files.data.value?.total ?? 0) - items.length }} left)</Button>
      </div>
    </template>

    <Sheet v-model:open="sheetOpen">
      <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-xl">
        <template v-if="selected">
          <SheetHeader>
            <SheetTitle class="pr-6 break-all">{{ selected.name }}</SheetTitle>
            <SheetDescription>{{ originLabel(selected.source) }} · {{ selected.mimeType }} · {{ size(selected.size) }}</SheetDescription>
          </SheetHeader>
          <div class="flex flex-col gap-5 px-4 pb-6">
            <div class="checkerboard grid max-h-96 min-h-40 place-items-center overflow-hidden rounded-lg border">
              <img v-if="isImage(selected)" :src="previewSrc(selected)" :alt="selected.name" class="h-80 w-full object-contain p-2" />
              <component :is="iconFor(selected)" v-else class="size-12 text-muted-foreground" aria-hidden="true" />
            </div>

            <div class="flex flex-wrap gap-2">
              <Button as-child size="sm"><a :href="fileUrl(selected.id, true)" :download="selected.name"><Download /> Download</a></Button>
              <Button size="sm" variant="outline" @click="copy(selected.uri), toast.success('Reference copied — paste it into a chat or tool.')"><Copy /> Copy reference</Button>
              <Button v-if="can('share.write')" size="sm" variant="outline" @click="share(selected)"><Share2 /> Share…</Button>
              <Button size="sm" variant="outline" class="text-destructive" @click="remove(selected)"><Trash2 /> Delete</Button>
            </div>

            <section v-if="actions.length" aria-labelledby="use-with">
              <h3 id="use-with" class="eyebrow mb-2 flex items-center gap-1.5"><Wand2 class="size-3.5" /> Use with</h3>
              <ul class="grid gap-1.5 sm:grid-cols-2">
                <li v-for="a in actions" :key="a.id">
                  <RouterLink :to="a.to" class="surface surface-interactive flex h-full flex-col rounded-lg px-3 py-2 shadow-none">
                    <span class="text-sm font-medium">{{ a.label }}</span>
                    <span class="text-xs text-muted-foreground">{{ a.hint }}</span>
                  </RouterLink>
                </li>
              </ul>
            </section>

            <section aria-labelledby="file-details">
              <h3 id="file-details" class="eyebrow mb-2">Details</h3>
              <dl class="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
                <dt class="text-muted-foreground">Name</dt>
                <dd class="min-w-0">
                  <form v-if="renaming" class="flex gap-2" @submit.prevent="rename(selected)">
                    <Input v-model="newName" maxlength="200" class="h-8" autofocus aria-label="New name" />
                    <Button size="sm" type="submit">Save</Button>
                  </form>
                  <span v-else class="flex items-center gap-1 break-all">
                    {{ selected.name }}
                    <Button size="icon" variant="ghost" class="size-7" aria-label="Rename" @click="renaming = true"><Pencil class="size-3.5" /></Button>
                  </span>
                </dd>
                <dt class="text-muted-foreground">Reference</dt>
                <dd class="font-mono text-xs break-all">{{ selected.uri }}</dd>
                <dt class="text-muted-foreground">Created</dt>
                <dd>{{ formatDateTime(selected.createdAt) }}</dd>
                <dt class="text-muted-foreground">Expires</dt>
                <dd class="flex flex-wrap items-center gap-2">
                  <span :class="daysLeft(selected) <= 1 && 'text-destructive'"><Clock class="mr-1 inline size-3.5" />{{ formatDateTime(selected.expiresAt) }} ({{ daysLeft(selected) }} d)</span>
                  <SimpleSelect :model-value="undefined" :options="keepOptions" placeholder="Keep longer…" class="h-8 w-44" aria-label="Keep longer" @update:model-value="(v) => keep(selected!, v)" />
                </dd>
                <template v-for="d in details(selected)" :key="d.label + d.value">
                  <dt class="text-muted-foreground">{{ d.label }}</dt>
                  <dd class="min-w-0 break-words whitespace-pre-wrap">
                    <button v-if="d.file" type="button" class="text-primary hover:underline" @click="selectedId = d.file">{{ d.value }}</button>
                    <template v-else>{{ d.value }}</template>
                  </dd>
                </template>
              </dl>
            </section>
          </div>
        </template>
      </SheetContent>
    </Sheet>

    <ShareFileDialog v-model:open="shareOpen" :file="shareTarget" />
  </div>
</template>

<style scoped>
.checkerboard {
  background-color: var(--color-muted);
  background-image:
    linear-gradient(45deg, var(--color-background) 25%, transparent 25%), linear-gradient(-45deg, var(--color-background) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, var(--color-background) 75%), linear-gradient(-45deg, transparent 75%, var(--color-background) 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}
</style>
